// B2174 (#3137) — the BEGIN … COMMIT blocks of the query console.
//
// The server keeps no open transaction, and it refuses BEGIN, COMMIT and
// ROLLBACK as statements: a client sends the complete transaction in ONE
// `transaction()` call. The console keeps the block form, and it changes each
// block into one call here, as the CLI does (`crates/kinedb-bin/src/txn_block.rs`):
//
//   - BEGIN … COMMIT (also COMMIT WITH TOKEN '…') is one transaction.
//   - BEGIN … ROLLBACK is not sent.
//   - A BEGIN inside a block, a COMMIT or a ROLLBACK outside a block, and a
//     BEGIN with no end are errors, and the console sends nothing of the script.
//
// The input is the output of `splitSql`, which removes the comments.

const CONTROL = /^(BEGIN|COMMIT|ROLLBACK)\b/i;
const BEGIN = /^BEGIN(\s+SAFE)?$/i;
const COMMIT = /^COMMIT(?:\s+WITH\s+TOKEN\s+'((?:[^']|'')*)')?$/i;
const ROLLBACK = /^ROLLBACK$/i;

/** The control word of `sql`, as `{begin}`, `{commit, token}`, `{rollback}`, or null. */
function controlOf(sql) {
  const text = sql.trim();
  if (!CONTROL.test(text)) return null;
  if (BEGIN.test(text)) return { begin: true };
  if (ROLLBACK.test(text)) return { rollback: true };
  const commit = COMMIT.exec(text);
  if (commit) {
    return { commit: true, token: commit[1] === undefined ? null : commit[1].replace(/''/g, "'") };
  }
  throw new Error(`${text}: write BEGIN, COMMIT, COMMIT WITH TOKEN '…' or ROLLBACK`);
}

/**
 * The units of a script, in script order:
 * `{kind: 'stmt', sql}`, `{kind: 'txn', stmts, token}` or `{kind: 'rollback', count}`.
 *
 * @param {string[]} stmts the statements of `splitSql`.
 * @throws {Error} for an error of the block form; the caller sends nothing.
 */
export function planBlocks(stmts) {
  const units = [];
  let open = null;
  stmts.forEach((sql, i) => {
    let control;
    try {
      control = controlOf(sql);
    } catch (e) {
      throw new Error(`statement ${i + 1}: ${e.message}. Nothing was sent.`);
    }
    const refuse = (what) => { throw new Error(`statement ${i + 1}: ${what}. Nothing was sent.`); };
    if (control === null) {
      if (open) open.push(sql);
      else units.push({ kind: 'stmt', sql });
    } else if (control.begin) {
      if (open) refuse('BEGIN inside a BEGIN block: a block does not nest');
      open = [];
    } else if (control.commit) {
      if (!open) refuse('COMMIT with no BEGIN: there is no block to commit');
      units.push({ kind: 'txn', stmts: open, token: control.token });
      open = null;
    } else {
      if (!open) refuse('ROLLBACK with no BEGIN: there is no block to roll back');
      units.push({ kind: 'rollback', count: open.length });
      open = null;
    }
  });
  if (open) {
    throw new Error(`a BEGIN with no COMMIT or ROLLBACK (${open.length} statement(s) in the block). Nothing was sent.`);
  }
  return units;
}
